var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));

// cbl/cblSignIn.ts
import {
  createAbortSlot,
  noop,
  sleep,
  TimeoutError
} from "@amazon/vinyl-util";

// cbl/service/auth.ts
import {
  ErrorBodyType,
  isResponseError,
  requestWithRetry
} from "@amazon/vinyl-util";
var cblAuthConfig = {
  value: {
    host: "https://api.amazon.com"
  }
};
function getAuthEndpoint() {
  return cblAuthConfig.value.host;
}
async function createCodePair(codeData) {
  const data = {
    code_data: codeData
  };
  const response = await requestWithRetry(
    "https://api.amazon.com/auth/create/codepair",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": "*"
      },
      body: JSON.stringify(data)
    },
    {
      readErrorBody: ErrorBodyType.TEXT,
      serviceId: "auth/create/codepair"
    }
  );
  return response.json();
}
async function register(codeData, codePair) {
  var _a;
  const requestBody = {
    requested_token_type: ["bearer"],
    auth_data: {
      code_pair: {
        public_code: codePair.publicCode,
        private_code: codePair.privateCode
      }
    },
    registration_data: codeData
  };
  try {
    const response = await requestWithRetry(
      "https://api.amazon.com/auth/register",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": "*"
        },
        body: JSON.stringify(requestBody)
      },
      {
        readErrorBody: ErrorBodyType.TEXT,
        serviceId: "auth/register"
      }
    );
    const json = await response.json();
    return {
      registered: true,
      bearer: json.response.success.tokens.bearer
    };
  } catch (e) {
    if (isResponseError(e) && ((_a = e.response) == null ? void 0 : _a.status) === 401) {
      return {
        registered: false,
        bearer: null
      };
    } else {
      throw e;
    }
  }
}
async function requestToken(request) {
  const response = await requestWithRetry(
    `${getAuthEndpoint()}/auth/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": "*"
      },
      body: JSON.stringify(request)
    },
    {
      readErrorBody: ErrorBodyType.TEXT,
      serviceId: "auth/token"
    }
  );
  return response.json();
}

// cbl/cblSignIn.ts
var UnexpectedSignInError = class _UnexpectedSignInError extends Error {
  constructor(message) {
    super(message);
    Object.setPrototypeOf(this, _UnexpectedSignInError.prototype);
  }
};
function onDocumentVisible(cb) {
  if (typeof document === "undefined") return noop;
  const visibilityChangeHandler = () => {
    if (document.visibilityState === "visible") {
      cb();
    }
  };
  document.addEventListener("visibilitychange", visibilityChangeHandler);
  return () => document.removeEventListener(
    "visibilitychange",
    visibilityChangeHandler
  );
}
async function pollForAuthResponse(codeData, codePairResponse) {
  const sleepAbort = createAbortSlot();
  const visibilityChangeSub = onDocumentVisible(() => {
    sleepAbort.abort();
  });
  const timeout = Date.now() + codePairResponse.expires_in * 1e3;
  while (Date.now() < timeout) {
    const response = await register(codeData, {
      privateCode: codePairResponse.private_code,
      publicCode: codePairResponse.public_code
    });
    if (response.registered) {
      visibilityChangeSub();
      return response.bearer;
    }
    const interval = codePairResponse.polling_interval_in_seconds;
    if (!interval)
      throw new Error("Code linking provided no polling interval");
    await sleep(interval, sleepAbort.value).catch(() => void 0);
  }
  visibilityChangeSub();
  throw new TimeoutError(
    `auth timed out after ${codePairResponse.expires_in}s`
  );
}
async function requestTokenFromCodeData(codeData, refreshToken) {
  var _a;
  const token = await requestToken({
    requested_token_type: "access_token",
    app_name: (_a = codeData.app_name) != null ? _a : "unknown",
    app_version: codeData.app_version,
    source_token_type: "refresh_token",
    source_token: refreshToken
  });
  return __spreadProps(__spreadValues({}, token), {
    refresh_token: refreshToken
  });
}
async function cblSignIn(deps) {
  const { codeData, displayCodeCallback, persistence } = deps;
  let token = await (persistence == null ? void 0 : persistence.get());
  if (!token) {
    const codePairResponse = await createCodePair(codeData);
    const removeDisplay = displayCodeCallback(codePairResponse.public_code);
    token = tokenWithRefreshToAuthToken(
      await pollForAuthResponse(codeData, codePairResponse)
    );
    removeDisplay();
    persistence == null ? void 0 : persistence.set(token);
  }
  return createCblAuthTokenProvider(codeData, token, (authToken) => {
    persistence == null ? void 0 : persistence.set(authToken);
  });
}
function createCblAuthTokenProvider(codeData, token, onRefreshed) {
  return async () => {
    if (token.expiresAt > Date.now() + 60 * 5 * 1e3) {
      return token.accessToken;
    } else {
      token = await requestTokenFromCodeData(
        codeData,
        token.refreshToken
      ).then(tokenWithRefreshToAuthToken);
      await (onRefreshed == null ? void 0 : onRefreshed(token));
      return token.accessToken;
    }
  };
}
function tokenWithRefreshToAuthToken(token) {
  return {
    accessToken: token.access_token,
    expiresAt: parseInt(token.expires_in) * 1e3 + Date.now(),
    refreshToken: token.refresh_token
  };
}

// cbl/service/stratus.ts
import { ErrorBodyType as ErrorBodyType2, requestWithRetry as requestWithRetry2 } from "@amazon/vinyl-util";
var stratusConfig = {
  value: {
    host: "https://music.amazon.com",
    region: "NA"
  }
};
function getStratusEndpoint() {
  const config = stratusConfig.value;
  return `${config.host}/${config.region}/api/stratus/`;
}
async function retrieveCustomerHome(requestHeaders, requestBody) {
  const response = await requestWithRetry2(
    getStratusEndpoint(),
    {
      headers: {
        Authority: "music.amazon.com",
        "Content-Encoding": "amz-1.0",
        "Content-Type": "application/json",
        "Accept-Language": "*",
        Authorization: `Bearer ${requestHeaders.oAuthToken}`,
        Pragma: "no-cache",
        "X-Amz-Target": "com.amazon.stratus.StratusServiceExternal.retrieveCustomerHome"
      },
      body: JSON.stringify(requestBody),
      method: "POST"
    },
    {
      readErrorBody: ErrorBodyType2.TEXT,
      serviceId: "api/stratus"
    }
  );
  return response.json();
}
async function retrieveBenefits(requestHeaders, requestBody) {
  const response = await requestWithRetry2(getStratusEndpoint(), {
    headers: {
      Authority: "music.amazon.com",
      "Content-Encoding": "amz-1.0",
      "Content-Type": "application/json",
      "Accept-Language": "*",
      Authorization: `Bearer ${requestHeaders.oAuthToken}`,
      Pragma: "no-cache",
      "X-Amz-Target": "com.amazon.stratus.StratusServiceExternal.retrieveBenefits"
    },
    body: JSON.stringify(requestBody),
    method: "POST"
  });
  return response.json();
}
export {
  UnexpectedSignInError,
  cblAuthConfig,
  cblSignIn,
  createCodePair,
  register,
  requestToken,
  requestTokenFromCodeData,
  retrieveBenefits,
  retrieveCustomerHome,
  stratusConfig
};
//# sourceMappingURL=index.js.map
